import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Star, Image as ImageIcon, Upload, Loader2, Edit } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';

const GalleryManagement = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [showVehiclePrompt, setShowVehiclePrompt] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [vehicleInfo, setVehicleInfo] = useState({ make: '', model: '', type: 'after' });
  const [formData, setFormData] = useState({
    url: '',
    title: '',
    category: 'exterior',
    type: 'after',
    description: '',
    featured: false
  });

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/gallery`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPhotos(data);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloudinaryUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    // Store files and show vehicle info prompt
    setPendingFiles(files);
    setShowVehiclePrompt(true);
    
    // Reset file input
    e.target.value = '';
  };

  const proceedWithUpload = async () => {
    if (!vehicleInfo.make || !vehicleInfo.model) {
      toast.error('Please enter vehicle make and model');
      return;
    }

    setShowVehiclePrompt(false);
    setUploading(true);
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const vehicleTitle = `${vehicleInfo.make} ${vehicleInfo.model}`;

    try {
      for (const file of pendingFiles) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`);
          continue;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 10MB)`);
          continue;
        }

        toast.info(`Uploading ${file.name}...`);

        // Get Cloudinary signature for gallery folder
        const sigResponse = await fetch(`${API_URL}/api/photos/cloudinary/signature?folder=mobile-shine/gallery`, {
          credentials: 'include'
        });

        if (!sigResponse.ok) {
          toast.error('Failed to get upload signature');
          continue;
        }

        const sig = await sigResponse.json();

        // Upload to Cloudinary
        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', sig.api_key);
        formData.append('timestamp', sig.timestamp);
        formData.append('signature', sig.signature);
        formData.append('folder', sig.folder);

        const uploadResponse = await fetch(
          `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
          {
            method: 'POST',
            body: formData
          }
        );

        if (!uploadResponse.ok) {
          toast.error(`Failed to upload ${file.name}`);
          continue;
        }

        const uploadData = await uploadResponse.json();

        // Save to gallery with Cloudinary URL
        const saveResponse = await fetch(`${API_URL}/api/gallery`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            url: uploadData.secure_url,
            title: vehicleTitle, // Use vehicle make/model as title
            category: 'exterior',
            type: vehicleInfo.type, // Use selected type (before/after/process)
            description: '',
            featured: false
          })
        });

        if (saveResponse.ok) {
          toast.success(`${file.name} uploaded successfully!`);
        } else {
          toast.error(`Failed to save ${file.name}`);
        }
      }

      // Refresh gallery
      fetchPhotos();
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setPendingFiles([]);
      setVehicleInfo({ make: '', model: '', type: 'after' });
    }
  };

  const handleAddPhoto = async (e) => {
    e.preventDefault();
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/gallery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Photo added to gallery!');
        setFormData({
          url: '',
          title: '',
          category: 'exterior',
          type: 'after',
          description: '',
          featured: false
        });
        setShowAddForm(false);
        fetchPhotos();
      } else {
        toast.error('Failed to add photo');
      }
    } catch (error) {
      toast.error('Error adding photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/gallery/${photoId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Photo deleted');
        fetchPhotos();
      } else {
        toast.error('Failed to delete photo');
      }
    } catch (error) {
      toast.error('Error deleting photo');
    }
  };

  const toggleFeatured = async (photoId, currentFeatured) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/gallery/${photoId}/featured?featured=${!currentFeatured}`, {
        method: 'PATCH',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success(!currentFeatured ? 'Photo featured!' : 'Photo unfeatured');
        fetchPhotos();
      }
    } catch (error) {
      toast.error('Error updating photo');
    }
  };

  const handleEditPhoto = (photo) => {
    setEditingPhoto(photo);
    setFormData({
      url: photo.url,
      title: photo.title,
      category: photo.category,
      type: photo.type,
      description: photo.description || '',
      featured: photo.featured
    });
  };

  const handleUpdatePhoto = async (e) => {
    e.preventDefault();

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/gallery/${editingPhoto.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Photo updated successfully!');
        setEditingPhoto(null);
        setFormData({
          url: '',
          title: '',
          category: 'exterior',
          type: 'after',
          description: '',
          featured: false
        });
        fetchPhotos();
      } else {
        toast.error('Failed to update photo');
      }
    } catch (error) {
      console.error('Error updating photo:', error);
      toast.error('Error updating photo');
    }
  };

  if (loading) {
    return <div className="text-white">Loading gallery...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Gallery Management</h2>
          <p className="text-gray-400">Manage your portfolio photos</p>
        </div>
        <div className="flex gap-2">
          {/* Cloudinary Upload Button */}
          <label htmlFor="cloudinary-upload">
            <Button
              type="button"
              disabled={uploading}
              className="bg-green-600 hover:bg-green-700 cursor-pointer"
              onClick={() => document.getElementById('cloudinary-upload').click()}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Photo
                </>
              )}
            </Button>
          </label>
          <input
            id="cloudinary-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={handleCloudinaryUpload}
            className="hidden"
          />
          
          {/* Manual URL Add Button */}
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            variant="outline"
            className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add URL
          </Button>
        </div>
      </div>

      {/* Add Photo Form (Manual URL) */}
      {showAddForm && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Add Photo by URL</CardTitle>
            <CardDescription className="text-gray-400">
              Paste a direct image URL (for advanced users)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddPhoto} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-200">Image URL</Label>
                <Input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
                <p className="text-xs text-gray-500">
                  Must be a direct image URL (ending in .jpg, .png, .webp, etc.)
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-200">Title</Label>
                <Input
                  type="text"
                  placeholder="e.g., BMW 3 Series - Exterior Detail"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-200">Category</Label>
                  <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exterior">Exterior</SelectItem>
                      <SelectItem value="interior">Interior</SelectItem>
                      <SelectItem value="wheels">Wheels</SelectItem>
                      <SelectItem value="headlights">Headlights</SelectItem>
                      <SelectItem value="ceramic">Ceramic Coating</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-200">Type</Label>
                  <Select value={formData.type} onValueChange={(val) => setFormData({ ...formData, type: val })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="before">Before</SelectItem>
                      <SelectItem value="after">After</SelectItem>
                      <SelectItem value="process">In Progress</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-200">Description (Optional)</Label>
                <Input
                  type="text"
                  placeholder="Additional details about this photo"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="featured" className="text-gray-200 cursor-pointer">
                  Featured (show prominently on homepage)
                </Label>
              </div>

              <div className="flex space-x-3">
                <Button type="submit" className="bg-red-600 hover:bg-red-700">
                  <Upload className="w-4 h-4 mr-2" />
                  Add to Gallery
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  variant="outline"
                  className="border-zinc-700 text-white hover:bg-zinc-800"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Vehicle Info Prompt Modal */}
      {showVehiclePrompt && (
        <Card className="bg-zinc-900 border-zinc-800 border-2 border-blue-500">
          <CardHeader>
            <CardTitle className="text-white">Vehicle Information</CardTitle>
            <CardDescription className="text-gray-400">
              Enter vehicle details for {pendingFiles.length} photo{pendingFiles.length > 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-200">Vehicle Make *</Label>
                  <Input
                    type="text"
                    placeholder="e.g., Toyota"
                    value={vehicleInfo.make}
                    onChange={(e) => setVehicleInfo({ ...vehicleInfo, make: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-200">Vehicle Model *</Label>
                  <Input
                    type="text"
                    placeholder="e.g., Camry"
                    value={vehicleInfo.model}
                    onChange={(e) => setVehicleInfo({ ...vehicleInfo, model: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-200">Photo Type *</Label>
                <Select
                  value={vehicleInfo.type}
                  onValueChange={(value) => setVehicleInfo({ ...vehicleInfo, type: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="before">Before</SelectItem>
                    <SelectItem value="after">After</SelectItem>
                    <SelectItem value="process">Process</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-300">
                  This information will be used as the photo title and label on your website.
                </p>
              </div>

              <div className="flex space-x-3">
                <Button 
                  onClick={proceedWithUpload}
                  className="bg-green-600 hover:bg-green-700 flex-1"
                  disabled={!vehicleInfo.make || !vehicleInfo.model}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload {pendingFiles.length} Photo{pendingFiles.length > 1 ? 's' : ''}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowVehiclePrompt(false);
                    setPendingFiles([]);
                    setVehicleInfo({ make: '', model: '', type: 'after' });
                  }}
                  variant="outline"
                  className="border-zinc-700 text-white hover:bg-zinc-800"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Photo Modal */}
      {editingPhoto && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Edit Photo</CardTitle>
            <CardDescription className="text-gray-400">
              Update photo details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdatePhoto} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-200">Title</Label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-200">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="exterior">Exterior</SelectItem>
                      <SelectItem value="interior">Interior</SelectItem>
                      <SelectItem value="wheels">Wheels</SelectItem>
                      <SelectItem value="headlights">Headlights</SelectItem>
                      <SelectItem value="ceramic">Ceramic Coating</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-200">Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="before">Before</SelectItem>
                      <SelectItem value="after">After</SelectItem>
                      <SelectItem value="process">Process</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-200">Description (Optional)</Label>
                <Input
                  type="text"
                  placeholder="Brief description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="edit-featured" className="text-gray-200">
                  Featured Photo
                </Label>
              </div>

              <div className="flex space-x-2">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 flex-1">
                  Update Photo
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setEditingPhoto(null);
                    setFormData({
                      url: '',
                      title: '',
                      category: 'exterior',
                      type: 'after',
                      description: '',
                      featured: false
                    });
                  }}
                  variant="outline"
                  className="border-zinc-700 text-white hover:bg-zinc-800"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Gallery Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {photos.length === 0 ? (
          <Card className="col-span-full bg-zinc-900 border-zinc-800">
            <CardContent className="py-12 text-center">
              <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">No photos in gallery yet</p>
              <Button onClick={() => setShowAddForm(true)} className="bg-red-600 hover:bg-red-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Photo
              </Button>
            </CardContent>
          </Card>
        ) : (
          photos.map((photo) => (
            <Card key={photo.id} className="bg-zinc-900 border-zinc-800 overflow-hidden">
              <div className="relative aspect-video">
                <img
                  src={photo.url}
                  alt={photo.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                  }}
                />
                <div className="absolute top-2 right-2 flex space-x-2">
                  <button
                    onClick={() => handleEditPhoto(photo)}
                    className="p-2 rounded-full bg-blue-600/80 hover:bg-blue-600 hover:scale-110 transition-transform"
                  >
                    <Edit className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={() => toggleFeatured(photo.id, photo.featured)}
                    className={`p-2 rounded-full ${
                      photo.featured ? 'bg-yellow-500' : 'bg-zinc-800/80'
                    } hover:scale-110 transition-transform`}
                  >
                    <Star className={`w-4 h-4 ${photo.featured ? 'text-white fill-white' : 'text-gray-400'}`} />
                  </button>
                  <button
                    onClick={() => handleDeletePhoto(photo.id)}
                    className="p-2 rounded-full bg-red-600/80 hover:bg-red-600 hover:scale-110 transition-transform"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
                <div className="absolute bottom-2 left-2 flex space-x-2">
                  <span className="px-2 py-1 bg-zinc-800/90 text-white text-xs rounded">
                    {photo.category}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded ${
                    photo.type === 'before' ? 'bg-orange-600/90 text-white' :
                    photo.type === 'after' ? 'bg-green-600/90 text-white' :
                    'bg-blue-600/90 text-white'
                  }`}>
                    {photo.type}
                  </span>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="text-white font-semibold">{photo.title}</h3>
                {photo.description && (
                  <p className="text-gray-400 text-sm mt-1">{photo.description}</p>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default GalleryManagement;
